/**
 * CRDT schema v2 timings join (LG-2848).
 *
 * Pure function: tokenizes LG-2847's derived runs, joins each word to `doc["timings"][uttId]` by
 * cumulative position within that utterance, and recomputes paragraph-relative char offsets.
 * Does not import Yjs — timings are a plain map (LG-2850 extracts it from the live doc).
 */

import { LANGUAGE_LOCALES, Language, LANGUAGES_WITHOUT_WHITESPACES, LanguageWithoutWhitespaces } from 'src/models/Language'

import { DerivedParagraph, DerivedWord, TimedParagraph, TimedRun } from './derived'

/**
 * Plain, Yjs-free timings sidecar: utterance id → per-word `[start, end]` pairs.
 */
export type TimingsMap = Partial<Record<string, Array<[number, number]>>>

type Token = { text: string; start: number }

const tokenizeWhitespace = (text: string): Token[] => [...text.matchAll(/\S+/g)].map((match) => ({ text: match[0], start: match.index }))

const tokenizeCjk = (text: string, segmenter: Intl.Segmenter): Token[] => {
    const tokens: Token[] = []

    for (const { segment, index, isWordLike } of segmenter.segment(text)) {
        if (isWordLike) {
            tokens.push({ text: segment, start: index })
        }
    }

    return tokens
}

const isNonWhitespaceLanguage = (language: Language): language is LanguageWithoutWhitespaces =>
    (LANGUAGES_WITHOUT_WHITESPACES as readonly Language[]).includes(language)

const tokenizerFor = (language: Language): ((text: string) => Token[]) => {
    if (!isNonWhitespaceLanguage(language)) {
        return tokenizeWhitespace
    }

    // One Segmenter per joinTimings call — constructing it per run is too expensive at session scale.
    const segmenter = new Intl.Segmenter(LANGUAGE_LOCALES[language][0], { granularity: 'word' })

    return (text) => tokenizeCjk(text, segmenter)
}

const takeWordTiming = (
    utteranceId: string | null,
    timings: TimingsMap,
    utteranceWordIndex: Map<string, number>,
): { start: number | null; end: number | null } => {
    if (utteranceId === null) {
        return { start: null, end: null }
    }

    const index = utteranceWordIndex.get(utteranceId) ?? 0
    utteranceWordIndex.set(utteranceId, index + 1)
    const pair = timings[utteranceId]?.[index]

    return { start: pair?.[0] ?? null, end: pair?.[1] ?? null }
}

const paragraphTimes = (runs: TimedRun[]): { startTime: number | null; endTime: number | null } => {
    let startTime: number | null = null
    let endTime: number | null = null

    for (const run of runs) {
        for (const word of run.words) {
            if (word.start === null) {
                continue
            }
            if (startTime === null) {
                startTime = word.start
            }
            if (word.end !== null) {
                endTime = word.end
            }
        }
    }

    return { startTime, endTime }
}

/**
 * Join per-word timings and recompute paragraph-relative char offsets onto LG-2847's derived paragraphs.
 *
 * Pure — never mutates its inputs or any document. `language` selects whitespace vs CJK tokenization.
 * `utteranceWordIndex` optionally seeds the cumulative per-utterance word index (LG-2849 window join);
 * the map is copied before use.
 */
export const joinTimings = (
    paragraphs: DerivedParagraph[],
    timings: TimingsMap,
    language: Language,
    utteranceWordIndex?: Map<string, number>,
): TimedParagraph[] => {
    const index = new Map(utteranceWordIndex)
    const tokenize = tokenizerFor(language)

    return paragraphs.map((paragraph) => {
        let charOffset = 0
        const runs: TimedRun[] = paragraph.runs.map((run) => {
            const tokens = run.text.length === 0 ? [] : tokenize(run.text)
            const words: DerivedWord[] = tokens.map((token) => {
                const charStart = charOffset + token.start

                return {
                    text: token.text,
                    charStart,
                    charEnd: charStart + token.text.length,
                    ...takeWordTiming(run.utteranceId, timings, index),
                }
            })

            charOffset += run.text.length

            return { ...run, words }
        })

        return {
            id: paragraph.id,
            speakerId: paragraph.speakerId,
            qac: paragraph.qac,
            runs,
            ...paragraphTimes(runs),
        }
    })
}
